import type { FreeCamera, Scene } from "@babylonjs/core";
import { Vector3 } from "@babylonjs/core";

export const PONTIANAK_SHAKE_MAX_HORIZONTAL=0.018;
export const PONTIANAK_SHAKE_MAX_VERTICAL=0.014;
export const PONTIANAK_SHAKE_MAX_ROLL_RADIANS=0.32*Math.PI/180;

function lerp(from:number,to:number,amount:number):number{return from+(to-from)*Math.max(0,Math.min(1,amount));}

export function pontianakShakeIntensity(distance:number,aggressive:boolean,active=true):number{
  if(!aggressive||!active||!Number.isFinite(distance)||distance>=12)return 0;
  if(distance>=8)return lerp(0,.18,(12-distance)/4);
  if(distance>=4)return lerp(.18,.58,(8-distance)/4);
  if(distance>=1)return lerp(.58,1,(4-distance)/3);
  return 1;
}

/** Applies a render-only offset and restores the authoritative camera immediately after each frame. */
export class ProximityCameraShake{
  private intensity=0;
  private targetIntensity=0;
  private elapsed=0;
  private applied=false;
  private readonly savedPosition=Vector3.Zero();
  private savedRoll=0;
  private readonly offset=Vector3.Zero();
  private readonly reducedMotion=typeof matchMedia!=="undefined"&&matchMedia("(prefers-reduced-motion: reduce)").matches;
  constructor(private readonly scene:Scene,private readonly camera:FreeCamera){
    scene.onBeforeRenderObservable.add(this.applyForRender);
    scene.onAfterRenderObservable.add(this.restoreAfterRender);
  }
  update(deltaSeconds:number,targetIntensity:number):void{
    this.elapsed+=Math.max(0,deltaSeconds);
    this.targetIntensity=this.reducedMotion?0:Math.max(0,Math.min(1,targetIntensity));
    const response=this.targetIntensity>this.intensity?8:6;
    const alpha=1-Math.exp(-Math.max(0,deltaSeconds)*response);
    this.intensity+=(this.targetIntensity-this.intensity)*alpha;
    if(this.intensity<.0001&&this.targetIntensity===0)this.intensity=0;
  }
  stop():void{this.restoreAfterRender();this.targetIntensity=0;this.intensity=0;this.offset.setAll(0);}
  dispose():void{this.stop();this.scene.onBeforeRenderObservable.removeCallback(this.applyForRender);this.scene.onAfterRenderObservable.removeCallback(this.restoreAfterRender);}
  get currentIntensity():number{return this.intensity;}
  get currentOffset():Vector3{return this.offset.clone();}
  get enabled():boolean{return!this.reducedMotion;}
  private readonly applyForRender=():void=>{
    if(this.applied||this.intensity<=0)return;
    this.savedPosition.copyFrom(this.camera.position);this.savedRoll=this.camera.rotation.z;
    const x=(Math.sin(this.elapsed*17.3)+Math.sin(this.elapsed*31.1+.7)*.36)*PONTIANAK_SHAKE_MAX_HORIZONTAL*this.intensity/.96;
    const y=(Math.sin(this.elapsed*19.7+1.2)+Math.sin(this.elapsed*27.9)*.28)*PONTIANAK_SHAKE_MAX_VERTICAL*this.intensity/.96;
    const roll=Math.sin(this.elapsed*14.9+.4)*PONTIANAK_SHAKE_MAX_ROLL_RADIANS*this.intensity;
    this.offset.set(x,y,0);this.camera.position.addInPlace(this.offset);this.camera.rotation.z+=roll;this.applied=true;
  };
  private readonly restoreAfterRender=():void=>{if(!this.applied)return;this.camera.position.copyFrom(this.savedPosition);this.camera.rotation.z=this.savedRoll;this.applied=false;};
}
